/**
 * Wallet Page
 * 
 * Passport Wallet - Central hub for:
 * - Event Tickets (live)
 * - Wallet Balance (pending - add funds)
 * - Creative NFTs (pending - mint, hold, sell)
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  WalletIcon,
  TicketIcon,
  CreditCardIcon,
  SparklesIcon,
  PlusIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { TicketCard, Ticket } from '../components/passport/TicketCard';
import { supabase } from '../lib/supabaseClient';

// =============================================================================
// TYPES
// =============================================================================

interface WalletBalance {
  availableBalanceCents: number;
  pendingBalanceCents: number;
  totalBalanceCents: number;
}

interface WalletStats {
  totalTickets: number;
  activeTickets: number;
  redeemedTickets: number;
  totalNFTs: number;
}

// =============================================================================
// COMPONENT
// =============================================================================

export default function WalletPage() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [balance, setBalance] = useState<WalletBalance | null>(null);
  const [stats, setStats] = useState<WalletStats | null>(null);
  const [filter, setFilter] = useState<'upcoming' | 'past' | 'all'>('upcoming');
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);

  const [recentTransactions, setRecentTransactions] = useState<any[]>([]);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  // Fetch wallet data
  useEffect(() => {
    fetchWalletData();
  }, [filter]);

  const fetchWalletData = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Fetch tickets
      let query = supabase
        .from('event_tickets')
        .select('*')
        .eq('user_id', user.id)
        .order('event_date', { ascending: true });

      // Apply filter
      const now = new Date().toISOString();
      if (filter === 'upcoming') {
        query = query.gte('event_date', now).eq('status', 'active');
      } else if (filter === 'past') {
        query = query.lt('event_date', now);
      }

      const { data: ticketsData, error: ticketsError } = await query;

      if (ticketsError) throw ticketsError;

      // Transform to Ticket type
      const transformedTickets: Ticket[] = (ticketsData || []).map(t => ({
        id: t.id,
        ticketNumber: t.ticket_number,
        qrCodeData: t.qr_code_data,
        event: {
          id: t.event_id,
          title: t.event_title,
          date: t.event_date,
          venue: t.event_venue || 'TBA',
          imageUrl: t.event_image_url
        },
        tier: t.tier,
        tierDisplayName: t.tier_display_name,
        priceCents: t.price_cents,
        perks: t.perks || [],
        status: t.status,
        redeemedAt: t.redeemed_at,
        validUntil: t.valid_until
      }));

      setTickets(transformedTickets);

      // Fetch wallet balance from live ledger via backend API
      try {
        const statusRes = await fetch(`${API_URL}/api/treasury/user-status/${user.id}`);
        if (statusRes.ok) {
          const statusData = await statusRes.json();
          setBalance({
            availableBalanceCents: statusData.balance,
            pendingBalanceCents: 0, // Pending payouts - future feature
            totalBalanceCents: statusData.balance
          });
        }
      } catch (apiErr) {
        console.warn('Could not fetch live balance from API, falling back to local calculation');
        // Fallback: calculate balance directly from ledger_entries
        const { data: ledgerData } = await supabase
          .from('ledger_entries')
          .select('amount_cents, type')
          .eq('user_id', user.id);

        if (ledgerData) {
          let bal = 0;
          for (const entry of ledgerData) {
            if (entry.type === 'credit') bal += entry.amount_cents;
            else if (entry.type === 'debit') bal -= entry.amount_cents;
          }
          setBalance({
            availableBalanceCents: bal,
            pendingBalanceCents: 0,
            totalBalanceCents: bal
          });
        }
      }

      // Fetch recent transactions from ledger
      try {
        const logsRes = await fetch(`${API_URL}/api/treasury/logs/${user.id}?limit=10`);
        if (logsRes.ok) {
          const logsData = await logsRes.json();
          setRecentTransactions(logsData.ledgerEntries || []);
        }
      } catch (logErr) {
        // Fallback: fetch directly
        const { data: txns } = await supabase
          .from('ledger_entries')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(10);
        setRecentTransactions(txns || []);
      }

      // Calculate stats
      const allTickets = transformedTickets;
      setStats({
        totalTickets: allTickets.length,
        activeTickets: allTickets.filter(t => t.status === 'active').length,
        redeemedTickets: allTickets.filter(t => t.status === 'redeemed').length,
        totalNFTs: 0 // Pending feature
      });

    } catch (error) {
      console.error('Error fetching wallet data:', error);
    } finally {
      setLoading(false);
    }
  };


  const formatCents = (cents: number) => {
    return (cents / 100).toFixed(2);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-black">
      <div className="max-w-7xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2 flex items-center gap-3">
              <WalletIcon className="w-10 h-10" />
              Passport Wallet
            </h1>
            <p className="text-gray-400">Your digital assets & experiences</p>
          </div>
        </div>

        {/* Wallet Balance Card - PENDING FEATURE */}
        <motion.div
          className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 backdrop-blur-xl border border-white/10 rounded-2xl p-6 mb-8 relative overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Live Balance Badge */}
          <div className="absolute top-4 right-4">
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-green-500/20 text-green-300 border border-green-500/30">
              <CheckCircleIcon className="w-3 h-3" />
              Live
            </span>
          </div>

          <div className="flex items-start justify-between mb-6">
            <div>
              <p className="text-gray-400 text-sm mb-2">Wallet Balance</p>
              <p className="text-5xl font-bold text-white">
                ${balance ? formatCents(balance.availableBalanceCents) : '0.00'}
              </p>
              {balance && balance.pendingBalanceCents > 0 && (
                <p className="text-sm text-gray-400 mt-1">
                  + ${formatCents(balance.pendingBalanceCents)} pending
                </p>
              )}
            </div>
            <CreditCardIcon className="w-12 h-12 text-purple-400" />
          </div>

          {/* Actions */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <button
              onClick={() => window.location.href = '/test-treasury'}
              className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-green-500/20 text-green-300 hover:bg-green-500/30 transition-all border border-green-500/20"
            >
              <PlusIcon className="w-5 h-5" />
              <span className="font-medium">Add Funds</span>
            </button>
            <button
              className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-white/5 text-white/40 cursor-not-allowed"
              disabled
            >
              <SparklesIcon className="w-5 h-5" />
              <span className="font-medium">Mint NFT</span>
            </button>
          </div>

          <p className="text-xs text-gray-500 text-center">
            💡 Balance calculated from Treasury ledger entries in real-time
          </p>
        </motion.div>

        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <motion.div
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4"
              whileHover={{ scale: 1.02 }}
            >
              <div className="flex items-center justify-between mb-2">
                <TicketIcon className="w-6 h-6 text-purple-400" />
                <span className="text-2xl font-bold text-white">{stats.totalTickets}</span>
              </div>
              <p className="text-sm text-gray-400">Total Tickets</p>
            </motion.div>

            <motion.div
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4"
              whileHover={{ scale: 1.02 }}
            >
              <div className="flex items-center justify-between mb-2">
                <CheckCircleIcon className="w-6 h-6 text-green-400" />
                <span className="text-2xl font-bold text-white">{stats.activeTickets}</span>
              </div>
              <p className="text-sm text-gray-400">Active</p>
            </motion.div>

            <motion.div
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4"
              whileHover={{ scale: 1.02 }}
            >
              <div className="flex items-center justify-between mb-2">
                <CheckCircleIcon className="w-6 h-6 text-gray-400" />
                <span className="text-2xl font-bold text-white">{stats.redeemedTickets}</span>
              </div>
              <p className="text-sm text-gray-400">Redeemed</p>
            </motion.div>

            <motion.div
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 opacity-50"
              whileHover={{ scale: 1.02 }}
            >
              <div className="flex items-center justify-between mb-2">
                <SparklesIcon className="w-6 h-6 text-purple-400" />
                <span className="text-2xl font-bold text-white">{stats.totalNFTs}</span>
              </div>
              <p className="text-sm text-gray-400">NFTs (Soon)</p>
            </motion.div>
          </div>
        )}

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6">
          {(['upcoming', 'past', 'all'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setFilter(tab)}
              className={`px-6 py-3 rounded-xl font-medium transition-all ${filter === tab
                ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/50'
                : 'bg-white/10 text-gray-400 hover:bg-white/20 hover:text-white'
                }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Tickets Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
          </div>
        ) : tickets.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence>
              {tickets.map((ticket, idx) => (
                <motion.div
                  key={ticket.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <TicketCard
                    ticket={ticket}
                    onClick={() => setSelectedTicket(ticket)}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        ) : (
          <motion.div
            className="text-center py-20"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <TicketIcon className="w-20 h-20 mx-auto text-gray-600 mb-6" />
            <h3 className="text-2xl font-bold text-white mb-2">No tickets yet</h3>
            <p className="text-gray-400 text-lg mb-6">
              {filter === 'upcoming'
                ? 'Purchase tickets to upcoming events to see them here'
                : filter === 'past'
                  ? 'No past events found'
                  : 'Your ticket wallet is empty'}
            </p>
            <button
              onClick={() => window.location.href = '/events'}
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium hover:shadow-lg hover:shadow-purple-500/50 transition-all"
            >
              Browse Events
            </button>
          </motion.div>
        )}

        {/* Transaction History */}
        {recentTransactions.length > 0 && (
          <motion.div
            className="mt-8 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <CreditCardIcon className="w-5 h-5 text-purple-400" />
                Transaction History
              </h3>
              <button
                onClick={fetchWalletData}
                className="text-sm text-purple-400 hover:text-purple-300 transition-colors"
              >
                Refresh
              </button>
            </div>
            <div className="space-y-2">
              {recentTransactions.map((tx: any, i: number) => (
                <motion.div
                  key={tx.id || i}
                  className="flex items-center justify-between p-3 bg-white/5 rounded-xl hover:bg-white/10 transition-colors"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${tx.type === 'credit' ? 'bg-green-500/20' : 'bg-red-500/20'
                      }`}>
                      <span className="text-sm">{tx.type === 'credit' ? '↓' : '↑'}</span>
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">
                        {tx.description || tx.event_source || 'Transaction'}
                      </p>
                      <p className="text-gray-500 text-xs">
                        {new Date(tx.created_at).toLocaleDateString('en-US', {
                          month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                  <span className={`font-mono font-semibold ${tx.type === 'credit' ? 'text-green-400' : 'text-red-400'
                    }`}>
                    {tx.type === 'credit' ? '+' : '-'}${formatCents(tx.amount_cents)}
                  </span>
                </motion.div>
              ))}
            </div>
            <div className="mt-4 text-center">
              <a
                href="/test-treasury"
                className="text-sm text-purple-400 hover:text-purple-300 transition-colors"
              >
                View all in Treasury Console →
              </a>
            </div>
          </motion.div>
        )}

        {/* NFT Section - Coming Soon */}
        <motion.div
          className="mt-12 bg-gradient-to-br from-indigo-500/10 to-purple-500/10 backdrop-blur-sm border border-white/10 rounded-2xl p-8 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <SparklesIcon className="w-16 h-16 mx-auto text-purple-400 mb-4" />
          <h3 className="text-2xl font-bold text-white mb-2">Creative NFTs</h3>
          <p className="text-gray-400 mb-4 max-w-2xl mx-auto">
            Soon you'll be able to mint, collect, and trade exclusive digital collectibles
            from your favorite artists. Music stems, behind-the-scenes footage, limited edition
            artwork, and more - all hosted in your Passport Wallet.
          </p>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/20 text-purple-300 text-sm">
            <ClockIcon className="w-4 h-4" />
            <span className="font-medium">Coming Soon</span>
          </div>
        </motion.div>
      </div>

      {/* Ticket Detail Modal */}
      <AnimatePresence>
        {selectedTicket && (
          <motion.div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedTicket(null)}
          >
            <motion.div
              className="max-w-md w-full"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <TicketCard ticket={selectedTicket} variant="full" />
              <button
                onClick={() => setSelectedTicket(null)}
                className="mt-4 w-full py-3 rounded-xl bg-white/10 text-white font-medium hover:bg-white/20 transition-colors"
              >
                Close
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

